import { Renderer } from '@json-render/react';
import { FC, ReactNode, useCallback, useRef, useState } from 'react';
import { useMount, useUnmount } from 'react-use';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icon,
  Stack,
  Text,
} from '@eluve/components';
import { useAnalytics } from '@eluve/posthog-react';
import { type ExpandableAnalytics, type Spec } from '@eluve/sdui-types';

import { useRegistry } from './RegistryContext';
import { useSDUIAnalyticsContext } from './SDUIAnalyticsContext';
import { useSDUIFeedbackContext } from './SDUIFeedbackContext';

export interface ExpandableRendererProps {
  title: string;
  content?: string;
  titleLinksSpec?: Spec;
  defaultOpen?: boolean;
  analytics?: ExpandableAnalytics;
  feedbackMetadata?: Record<string, unknown>;
  children?: ReactNode;
}

export const ExpandableRenderer: FC<ExpandableRendererProps> = ({
  title,
  content,
  titleLinksSpec,
  defaultOpen = false,
  analytics,
  feedbackMetadata,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const expandedAtRef = useRef<number | null>(null);
  const unregisterRef = useRef<(() => void) | null>(null);
  const { capture } = useAnalytics();
  const sduiAnalytics = useSDUIAnalyticsContext();
  const sduiFeedback = useSDUIFeedbackContext();
  const registry = useRegistry();

  // Initialize expanded state on mount if defaultOpen is true
  useMount(() => {
    if (defaultOpen) {
      expandedAtRef.current = Date.now();
      if (!unregisterRef.current) {
        unregisterRef.current =
          sduiAnalytics?.registerExpandedSection?.() ?? null;
      }
    }
  });

  // Report any remaining expanded time on unmount
  useUnmount(() => {
    unregisterRef.current?.();
    if (expandedAtRef.current !== null) {
      const timeExpandedMs = Date.now() - expandedAtRef.current;
      sduiAnalytics?.onSectionCollapsed?.(timeExpandedMs);
      // Fire data-driven analytics event on unmount
      if (analytics?.onCollapse) {
        capture(analytics.onCollapse.event, {
          ...analytics.onCollapse.properties,
          appointmentId: sduiAnalytics?.appointmentId,
          view: sduiAnalytics?.view,
          time_expanded_ms: timeExpandedMs,
          collapsed_by: 'navigation',
        });
      }
    }
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        expandedAtRef.current = Date.now();
        // Register for real-time tracking
        unregisterRef.current =
          sduiAnalytics?.registerExpandedSection?.() ?? null;
        // Notify context for aggregation tracking
        sduiAnalytics?.onSectionExpanded?.();
        // Fire data-driven analytics event (backend provides all static props)
        if (analytics?.onExpand) {
          capture(analytics.onExpand.event, {
            ...analytics.onExpand.properties,
            // Runtime context only
            appointmentId: sduiAnalytics?.appointmentId,
            view: sduiAnalytics?.view,
            time_since_loaded_ms: sduiAnalytics?.getTimeSinceLoaded?.() ?? null,
          });
        }
      } else if (expandedAtRef.current !== null) {
        const timeExpandedMs = Date.now() - expandedAtRef.current;
        // Unregister from real-time tracking
        unregisterRef.current?.();
        unregisterRef.current = null;
        // Notify context for aggregation tracking
        sduiAnalytics?.onSectionCollapsed?.(timeExpandedMs);
        // Fire data-driven analytics event (backend provides all static props)
        if (analytics?.onCollapse) {
          capture(analytics.onCollapse.event, {
            ...analytics.onCollapse.properties,
            // Runtime context only
            appointmentId: sduiAnalytics?.appointmentId,
            view: sduiAnalytics?.view,
            time_expanded_ms: timeExpandedMs,
            collapsed_by: 'user_action',
          });
        }
        expandedAtRef.current = null;
      }
      setIsOpen(open);
    },
    [analytics, capture, sduiAnalytics],
  );

  return (
    <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
      <div className="rounded-lg border border-border-primary bg-bg-primary">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 p-3 text-left hover:bg-bg-secondary/50"
          >
            <span className="flex items-center gap-1">
              <Text inline size="xs" weight="bold">
                {title}
              </Text>
              {titleLinksSpec && (
                <div
                  className="inline-flex gap-0.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Renderer spec={titleLinksSpec} registry={registry} />
                </div>
              )}
            </span>
            <span className="shrink-0 text-text-tertiary">
              <Icon
                name="ChevronDown"
                className={`transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent animated>
          <div className="px-3 pb-3">
            {typeof content === 'string' ? (
              <Text size="xs" color="secondary">
                {content}
              </Text>
            ) : (
              <Stack gap={2} align="stretch">
                {children}
              </Stack>
            )}
          </div>
          {isOpen &&
          feedbackMetadata != null &&
          sduiFeedback?.renderFeedback ? (
            <div className="px-3 pb-3">
              {sduiFeedback.renderFeedback(feedbackMetadata)}
            </div>
          ) : null}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
